import { createApi } from "@reduxjs/toolkit/query/react";
import {
  ChatChannel,
  ChatCreateNewPayload,
  ChatSearchedUser,
  ChatSearchUsersPayload,
  SearchSMSUsersPayload,
  SendSMSPayload,
  SMSConversationPayload,
  SMSMessage,
  SMSUnreadCountResponse,
  SMSUser,
} from "../types/Chat";
import { ListingResponse } from "../types/Listing";
import { appBaseQuery, baseQueryWithAuthHandling } from "../utils/utils";
import { API_METHODS } from "./constants";
import { API_ENDPOINTS } from "./endpoints";
import { handleAuthErrorCode } from "./errorCodes";

export enum ChatApiTags {
  CreateChatChannel = "CreateChatChannel",
  SendSMS = "SendSMS",
  GetSMS = "GetSMS",
}

export const ChatAPIs = createApi({
  reducerPath: "chatApi",
  tagTypes: [
    ChatApiTags.CreateChatChannel,
    ChatApiTags.SendSMS,
    ChatApiTags.GetSMS,
  ],
  baseQuery: (args, api, extraOptions) =>
    baseQueryWithAuthHandling(
      appBaseQuery.withAuthJson,
      args,
      api,
      extraOptions
    ),
  endpoints: (builder) => ({
    // get all chat channels
    getAllChatChannels: builder.query<ListingResponse<ChatChannel[]>, void>({
      query: () => {
        return {
          url: API_ENDPOINTS.CHAT.GET_ALL_CHANNELS,
          method: API_METHODS.GET,
        };
      },
      providesTags: [ChatApiTags.CreateChatChannel],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<ChatChannel[]>) => {
        return response;
      },
    }),

    // search users
    searchChatUsers: builder.query<
      ListingResponse<ChatSearchedUser[]>,
      ChatSearchUsersPayload
    >({
      query: (params: ChatSearchUsersPayload) => {
        return {
          url: API_ENDPOINTS.CHAT.SEARCH_USERS,
          method: API_METHODS.GET,
          params,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<ChatSearchedUser[]>) => {
        return response;
      },
    }),

    // create chat channel
    createChatChannel: builder.mutation<ChatChannel, ChatCreateNewPayload>({
      query: (formData: ChatCreateNewPayload) => {
        return {
          url: API_ENDPOINTS.CHAT.CREATE_CHANNEL,
          method: API_METHODS.POST,
          body: formData,
        };
      },
      invalidatesTags: [ChatApiTags.CreateChatChannel],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ChatChannel) => {
        return response;
      },
    }),

    // search users
    sMSUsers: builder.query<ListingResponse<SMSUser[]>, SearchSMSUsersPayload>({
      query: (params: ChatSearchUsersPayload) => {
        return {
          url: API_ENDPOINTS.SMS.GET_DRIVERS,
          method: API_METHODS.GET,
          params,
        };
      },
      providesTags: [ChatApiTags.GetSMS],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<SMSUser[]>) => {
        return response;
      },
    }),

    getSMSUnreadCount: builder.query<SMSUnreadCountResponse, void>({
      query: () => {
        return {
          url: API_ENDPOINTS.SMS.GET_UNREAD_COUNT,
          method: API_METHODS.GET,
        };
      },
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: SMSUnreadCountResponse) => {
        return response;
      },
    }),

    // get SMS conversation
    getSMSCconversation: builder.query<
      ListingResponse<SMSMessage[]>,
      SMSConversationPayload
    >({
      query: (params: SMSConversationPayload) => {
        return {
          url: API_ENDPOINTS.SMS.GET_SMS,
          method: API_METHODS.GET,
          params,
        };
      },
      providesTags: [ChatApiTags.SendSMS],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: ListingResponse<SMSMessage[]>) => {
        return response;
      },
    }),

    sendSMS: builder.mutation<SendSMSPayload, SendSMSPayload>({
      query: (formData: SendSMSPayload) => {
        return {
          url: API_ENDPOINTS.SMS.GET_SMS,
          method: API_METHODS.POST,
          body: formData,
        };
      },
      // disabled the next line, as refetch is triggered after 3 seconds in
      // sendNewMessage function in smsConversation.tsx
      // ------------------------------------------------------------------
      // invalidatesTags: [ChatApiTags.SendSMS],
      transformErrorResponse(baseQueryReturnValue) {
        handleAuthErrorCode(baseQueryReturnValue);
        return baseQueryReturnValue;
      },
      transformResponse: (response: SendSMSPayload) => {
        return response;
      },
    }),
  }),
});

export const {
  useGetAllChatChannelsQuery,
  useSearchChatUsersQuery,
  useCreateChatChannelMutation,

  useSMSUsersQuery,
  useLazySMSUsersQuery,
  useGetSMSUnreadCountQuery,
  useGetSMSCconversationQuery,
  useSendSMSMutation,
} = ChatAPIs;
